import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowUpRight, ShoppingBag } from 'lucide-react';
import { Clock3 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isProductAvailable } from '@app-types/product';
import { Button } from '@components/common/Button';
import { useCart } from '@context/CartContext';
import { checkoutPath } from '@services/checkout/checkoutIntent';
import { Price, TaxExclusion } from './Price';
import { PlanDurationSelector } from './PlanDurationSelector';
import { getProductVariant, resolveProductVariant } from '@utils/productVariants';
import { formatCurrencyMinor } from '@utils/formatCurrency';
export const paymentIsPending = (product) => !product.stripePaymentLink || product.stripePaymentLink.includes('PLACEHOLDER');
export const PaymentNotice = ({ product }) => {
    const { t } = useTranslation();
    return paymentIsPending(product) ? _jsx(Pending, { children: t('interior.purchase.pending') }) : null;
};
export const ProductPurchase = ({ product, className }) => {
    const { t, i18n } = useTranslation();
    const { addProduct } = useCart();
    const navigate = useNavigate();
    const [variantId, setVariantId] = useState(product.variants?.[0]?.id);
    if (!isProductAvailable(product)) {
        return _jsx(ComingSoonPurchase, { product: product, className: className });
    }
    const variant = getProductVariant(product, variantId);
    const selectedProduct = resolveProductVariant(product, variantId);
    return (_jsxs(Purchase, { className: className, role: "group", "aria-label": `${t('interior.purchase.label')}: ${t(product.nameKey)}`, children: [product.variants && (_jsx(PlanDurationSelector, { product: product, selected: variant, onChange: (id) => {
                    setVariantId(id);
                } })), _jsxs(PriceLine, { "aria-live": variant ? 'polite' : undefined, "aria-atomic": "true", children: [variant && (_jsx(SelectedDuration, { children: t('personalCare360.selected', {
                            duration: t('personalCare360.duration', { months: variant.durationMonths })
                        }) })), _jsx("span", { children: t(variant ? 'personalCare360.periodTotal' : 'home.showcase.priceLabel') }), _jsxs(CurrentPrice, { children: [(!!variant?.savingsMinor || (!variant && product.listPriceMinor)) && (_jsx(OriginalPrice, { "aria-label": t('personalCare360.originalPrice'), children: _jsx(Price, { amountMinor: variant ? variant.referencePriceMinor : product.listPriceMinor, currency: product.currency }) })), _jsx("strong", { children: _jsx(Price, { amountMinor: selectedProduct?.priceMinor ?? product.priceMinor, currency: product.currency }) })] }), _jsx(TaxExclusion, {}), !!variant?.savingsMinor && (_jsxs(PurchaseSaving, { children: [t('personalCare360.savingsDetail', {
                                amount: formatCurrencyMinor(variant.savingsMinor, product.currency, i18n.language),
                                percent: variant.discountPercent
                            }), variant.bestValue && ` · ${t('personalCare360.bestValue')}`] })), variant?.labelKey && _jsx("span", { children: t(variant.labelKey) })] }), _jsxs(PurchaseActions, { children: [_jsxs(Button, { type: "button", onClick: () => navigate(checkoutPath(product, variantId)), children: [t('actions.buyNow'), _jsx(ArrowUpRight, { size: 16, "aria-hidden": "true" })] }), _jsxs(Button, { type: "button", "$variant": "secondary", onClick: () => addProduct(product.id, variantId), children: [_jsx(ShoppingBag, { size: 17, "aria-hidden": "true" }), t('actions.addToCart')] })] }), _jsx(PaymentNotice, { product: selectedProduct ?? product })] }));
};
export const ComingSoonPurchase = ({ product, className }) => {
    const { t } = useTranslation();
    const name = t(product.nameKey);
    return (_jsxs(ComingSoon, { className: className, role: "group", "aria-label": `${t('availability.comingSoon')}: ${name}`, children: [_jsxs(ComingSoonBadge, { role: "status", children: [_jsx(Clock3, { size: 16, "aria-hidden": "true" }), t('availability.comingSoon')] }), _jsx(ComingSoonText, { children: t('availability.inPreparation') }), _jsxs(ComingSoonButton, { type: "button", disabled: true, "aria-label": `${t('availability.comingSoon')}: ${name}`, children: [_jsx(Clock3, { size: 17, "aria-hidden": "true" }), t('availability.comingSoon')] })] }));
};
const Purchase = styled.div `
  min-width: 0;
`;
const ComingSoon = styled.div `
  display: grid;
  align-content: start;
  justify-items: start;
  min-width: 0;
  gap: 0.75rem;
`;
const ComingSoonBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.mint200};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  color: ${({ theme }) => theme.colors.navy900};
  background: ${({ theme }) => theme.colors.mint50};
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.4;
`;
const ComingSoonText = styled.p `
  margin: 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  line-height: 1.6;
`;
const ComingSoonButton = styled(Button) `
  min-width: min(100%, 13rem);
`;
const SelectedDuration = styled.p `
  margin: 0 0 0.3rem;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 0.875rem;
  font-weight: 700;
`;
const PriceLine = styled.div `
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1rem;
  > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
  }
  strong {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.04em;
  }
`;
const PurchaseActions = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  button {
    font-size: 0.875rem;
    padding-inline: 1rem;
    flex: 1 1 auto;
  }
`;
const CurrentPrice = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.4rem 0.85rem;
`;
const OriginalPrice = styled.s `
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1.125rem;
  white-space: nowrap;
  text-decoration-thickness: 1px;
`;
const PurchaseSaving = styled.span `
  color: ${({ theme }) => theme.colors.blue600} !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  line-height: 1.45;
`;
const Pending = styled.p `
  margin: 0.75rem 0 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.75rem;
  line-height: 1.6;
`;
export const PurchaseError = styled.p `
  margin: 0.75rem 0 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  line-height: 1.6;
`;
